import { useFormik } from "formik"
import * as Yup from "yup"
import React, { useMemo } from "react"
import toast from "react-hot-toast"
import {
  Button,
  Form,
  FormFeedback,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap"
import Spinners from "components/Common/Spinner"
import {
  useCreateSignersMutation,
  useUpdateSignersMutation,
} from "store/api/signers/signers.api"

const defaultValues = {
  name: "",
  designation: "",
  phone_no: "",
}

function CreateSignersModal({
  open,
  setOpen,
  title,
  onCreateSuccess,
  editData,
}) {
  const [createSigner, { isLoading }] = useCreateSignersMutation()
  const [updateSigner, { isLoading: isUpdating }] = useUpdateSignersMutation()

  const mutate = editData === null ? createSigner : updateSigner

  const initialValues = useMemo(() => {
    if (editData) {
      const { id, ...rest } = editData
      return { ...defaultValues, ...rest }
    } else {
      return { ...defaultValues }
    }
  }, [])

  const onFormSubmit = async (values, resetForm) => {
    try {
      const { message, success } = await mutate(values).unwrap()
      if (success) {
        toast.success(message, { id: "submitSuccess" })
        resetForm()
        onCreateSuccess()
        setOpen(false)
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }

      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const onFormUpdate = async (values, resetForm) => {
    try {
      const { data, message, success } = await mutate({
        ...values,
        id: editData?.id,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "editSuccess" })
        resetForm()
        onCreateSuccess()
        setOpen(false)
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({}),
    onSubmit: async (values, { resetForm }) => {
      if (editData === null) {
        await onFormSubmit(values, resetForm)
      } else {
        onFormUpdate(values, resetForm)
      }
    },
  })

  return (
    <Modal
      isOpen={open}
      toggle={() => {
        setOpen((prev) => !prev)
      }}
    >
      <ModalHeader
        toggle={() => {
          setOpen((prev) => !prev)
        }}
      >
        {title} Signatory
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <div className="mb-2">
            <label htmlFor="name" className="col-form-label">
              Name:
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={validation.values.name || ""}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              className="form-control"
            />
            {validation.errors.name ? (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-2">
            <label htmlFor="designation" className="col-form-label">
              Designation
            </label>
            <input
              id="designation"
              name="designation"
              type="text"
              value={validation.values.designation || ""}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              className="form-control"
            />
            {validation.errors.designation ? (
              <FormFeedback type="invalid">
                {validation.errors.designation}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-2">
            <label htmlFor="phone_no" className="col-form-label">
              Phone No.
            </label>
            <input
              id="phone_no"
              name="phone_no"
              type="text"
              className="form-control"
              value={validation.values.phone_no || ""}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.errors.phone_no ? (
              <FormFeedback type="invalid">
                {validation.errors.phone_no}
              </FormFeedback>
            ) : null}
          </div>
          <Button type="submit" color="primary">
            {isLoading || isUpdating ? (
              <>
                <Spinners />
              </>
            ) : (
              <>{!editData ? `Create Signatory` : "Update Signatory"}</>
            )}
          </Button>
        </Form>
      </ModalBody>
    </Modal>
  )
}

export default CreateSignersModal
